import { type TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, LabelS } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_PLAY_PRO_ONBOARDING_LEADERBOARD_CHOOSE_TEAMS } from '@sorare/core/src/constants/__generated__/routes';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thinMaterial } from '@sorare/core/src/style/utils';

import { MarketGuidanceLayout } from 'components/onboarding/MarketGuidanceLayout';

import type {
  ChooseCompetitionQuery,
  ChooseCompetitionQueryVariables,
} from './__generated__/page.graphql';

const Competitions = styled(Vertical)`
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  @media ${tabletAndAbove} {
    grid-template-columns: repeat(4, 1fr);
  }
`;
const Competition = styled(Vertical).attrs({ gap: 1.5, center: true })`
  padding: var(--triple-unit) var(--double-unit);
  border-radius: var(--unit);
  background: var(--c-nl-500);
  ${thinMaterial}
  border: 1px solid var(--c-nd-50);

  &:hover {
    background: var(--c-nl-300);
  }
  transition: 300ms background-color ease-in-out;
`;
const Icon = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;

const CHOOSE_COMPETITION_QUERY = gql`
  query ChooseCompetitionQuery {
    currentUser {
      slug
      anyUserSportProfile(sport: FOOTBALL) {
        id
        onboardingLeaderboards {
          id
          displayName
          pictureUrl
          playableLeaderboard {
            slug
          }
        }
      }
    }
  }
` as TypedDocumentNode<ChooseCompetitionQuery, ChooseCompetitionQueryVariables>;

const ChooseCompetitionPage = () => {
  const isTabletAndAbove = useIsTabletAndAbove();

  const { data } = useQuery(CHOOSE_COMPETITION_QUERY);
  const { onboardingLeaderboards = [] } =
    data?.currentUser?.anyUserSportProfile || {};

  return (
    <MarketGuidanceLayout>
      <Vertical center={isTabletAndAbove} gap={3}>
        <Vertical gap={2.5}>
          <StarBall width={40} height={40} />
          <HeadlineL>
            <FormattedMessage
              id="ChooseCompetitionPage.explanation"
              defaultMessage="What competition do you want to focus on?"
            />
          </HeadlineL>
        </Vertical>
        <Competitions>
          {onboardingLeaderboards.map(
            ({ id, displayName, pictureUrl, playableLeaderboard }) =>
              playableLeaderboard && (
                <Competition
                  key={id}
                  as={Link}
                  to={generatePath(
                    FOOTBALL_PLAY_PRO_ONBOARDING_LEADERBOARD_CHOOSE_TEAMS,
                    { leaderboard: playableLeaderboard.slug }
                  )}
                >
                  <Icon src={pictureUrl} alt="" />
                  <LabelS bold className="text-center">
                    {displayName}
                  </LabelS>
                </Competition>
              )
          )}
        </Competitions>
      </Vertical>
    </MarketGuidanceLayout>
  );
};
export default ChooseCompetitionPage;
